'use client'

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'

import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { showSnackbar } from '@b-stock/fe-core/bstock-react'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import listingSearchQuery from '@/queries/listingSearchQuery'

import { COMPARE_LIMIT } from '../constants'
import {
  CompareSelectionContext,
  type CompareSelectionContextValue,
} from './CompareSelectionContext'

type State = {
  compareMode: boolean
  selectedIds: string[]
  isPanelOpen: boolean
}

type Action =
  | { type: 'TOGGLE_LISTING'; id: string }
  | { type: 'CLEAR' }
  | { type: 'SET_MODE'; compareMode: boolean }
  | { type: 'SET_PANEL_OPEN'; isPanelOpen: boolean }

const initialState: State = {
  compareMode: false,
  selectedIds: [],
  isPanelOpen: false,
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TOGGLE_LISTING': {
      if (state.selectedIds.includes(action.id)) {
        return {
          ...state,
          selectedIds: state.selectedIds.filter((id) => id !== action.id),
        }
      }
      // At limit: keep the reducer pure — the component fires the toast and
      // dispatches nothing, but guard here too so the cap always holds.
      if (state.selectedIds.length >= COMPARE_LIMIT) {
        return state
      }
      return { ...state, selectedIds: [...state.selectedIds, action.id] }
    }
    case 'CLEAR':
      return { ...state, selectedIds: [] }
    case 'SET_MODE':
      // Exiting compare mode clears the current selection.
      return action.compareMode
        ? { ...state, compareMode: true }
        : { compareMode: false, selectedIds: [], isPanelOpen: false }
    case 'SET_PANEL_OPEN':
      return { ...state, isPanelOpen: action.isPanelOpen }
  }
}

export const CompareSelectionProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { formatMessage } = useIntl()
  const { data, similarSearch } = useListingSearch()
  const [state, dispatch] = useReducer(reducer, initialState)

  // Evaluate the experiment flag exactly once per mount, the first time the
  // buyer is eligible to see compare at all — the same test CompareToggleButton
  // uses to decide whether to render. Keeps the experiment population to buyers
  // who could actually have seen the feature.
  const ldClient = useLDClient()
  const accountId = useDecodedAccessToken()?.accountId
  const isCompareEnabled = useFlag(FeatureFlags.ENABLE_ALP_COMPARE_LISTINGS)
  const compareExposedRef = useRef(false)
  useEffect(() => {
    if (
      !compareExposedRef.current &&
      ldClient &&
      accountId &&
      isCompareEnabled
    ) {
      compareExposedRef.current = true
      ldClient.variation(
        FeatureFlags.ENABLE_ALP_COMPARE_LISTINGS.key,
        FeatureFlags.ENABLE_ALP_COMPARE_LISTINGS.defaultValue
      )
    }
  }, [ldClient, accountId, isCompareEnabled])
  const { compareMode, selectedIds, isPanelOpen } = state
  const atLimit = selectedIds.length >= COMPARE_LIMIT

  // Every surface inside this provider that can render a compare checkbox has
  // to be represented here, or its selections become unresolvable: the id lands
  // in `selectedIds` (so the count and the cap move) while `selectedListings`
  // silently drops it, leaving the tray a box short and the panel a column
  // short. The Similar Inventory carousel is one such surface — it sits inside
  // this provider but reads a separate query, so its listings are folded in
  // alongside the main results. Main results come last so they win the
  // by-id cache when a listing appears in both.
  //
  // Wrap each raw result in a CombinedListingSearchResult — a facade (the
  // Facade design pattern) that exposes derived fields like price and bid state
  // over the raw API shape. Memoized so the cache computation below doesn't
  // re-run on every render (a fresh array would defeat its memo).
  const similarListings = similarSearch.data?.listings
  const rawListings = useMemo(
    () => [...(similarListings ?? []), ...(data?.listings ?? [])],
    [similarListings, data?.listings]
  )
  const facadeListings = useMemo(
    () => rawListings.map((raw) => new CombinedListingSearchResult(raw)),
    [rawListings]
  )

  // Accumulate the full CombinedListingSearchResult objects for selected
  // listings across page turns: add any on the current page whose listingId is
  // selected, evict deselected.
  const selectedListingsCacheRef = useRef<
    Map<string, CombinedListingSearchResult>
  >(new Map())
  const cachedSelectedListings = useMemo(() => {
    const cache = selectedListingsCacheRef.current
    for (const listing of facadeListings) {
      if (selectedIds.includes(listing.listingId)) {
        cache.set(listing.listingId, listing) // oxlint-disable-line react/refs
      }
    }
    // oxlint-disable-next-line react/refs
    for (const id of [...cache.keys()]) {
      if (!selectedIds.includes(id)) {
        cache.delete(id) // oxlint-disable-line react/refs
      }
    }
    // oxlint-disable-next-line react/refs
    return selectedIds.flatMap((id) => {
      const listing = cache.get(id)
      return listing ? [listing] : []
    })
  }, [facadeListings, selectedIds])

  // The cache above only sees the page on screen, so a selection from an
  // earlier page goes stale (BP-2731). Re-read the selection from search.
  //
  // Keyed off selectedIds, not the cache it feeds, so the query key stays a
  // pure function of the selection. The search `id` filter accepts listingId.
  const refreshParams = useMemo(
    () => ({
      params: { id: selectedIds, limit: COMPARE_LIMIT },
      usePostEndpoint: true as const,
      fetchFilters: false,
    }),
    [selectedIds]
  )
  // Not gated on isPanelOpen — the tray shows prices too, and would stay stale.
  // The length guard matters: an empty id list is an unfiltered catalog search.
  const { data: refreshedPage, refetch } = listingSearchQuery.useWithOpts(
    [refreshParams],
    {
      enabled: compareMode && selectedIds.length > 0,
      staleTime: 0,
      // Explicit: this is how cross-tab terms acceptance recovers (BP-2730).
      // 'always' so a future staleTime change can't silently break it.
      refetchOnWindowFocus: 'always',
    }
  )

  // The query stays mounted for the provider's life, so opening the panel has
  // to force the re-read itself — otherwise it shows the last fetch.
  useEffect(() => {
    if (isPanelOpen) void refetch()
  }, [isPanelOpen, refetch])
  const refreshedById = useMemo(
    () =>
      new Map(
        (refreshedPage?.listings ?? []).map((raw) => [
          raw.listingId,
          new CombinedListingSearchResult(raw),
        ])
      ),
    [refreshedPage?.listings]
  )

  // The re-read wins for every selection, on screen or not. Preferring the
  // on-screen copy assumed Pusher kept grid cards live; it does not always, and
  // a stale card could win a comparison row it had not earned. When a card is
  // stale the panel now disagrees with it — preferred over a wrong verdict.
  const selectedListings = useMemo(() => {
    if (refreshedById.size === 0) return cachedSelectedListings
    return cachedSelectedListings.map(
      (listing) => refreshedById.get(listing.listingId) ?? listing
    )
  }, [cachedSelectedListings, refreshedById])

  const toggleListing = useCallback(
    (id: string) => {
      // At limit and adding a new id: warn and don't dispatch. Deselecting an
      // already-selected id (or being under the limit) always proceeds.
      if (atLimit && !selectedIds.includes(id)) {
        const toastId = showSnackbar({
          content: formatMessage(
            { id: 'Listings.ListView.bulkActions.compareLimitToast' },
            { limit: COMPARE_LIMIT }
          ),
          options: { type: 'default', autoClose: true },
        })
        // SnackbarContainer's hideProgressBar suppresses toastify's built-in
        // auto-dismiss; setTimeout is a hard-deadline fallback.
        setTimeout(() => toast.dismiss(toastId), 6_000)
        return
      }
      dispatch({ type: 'TOGGLE_LISTING', id })
    },
    [atLimit, selectedIds, formatMessage]
  )

  const clearSelection = useCallback(() => dispatch({ type: 'CLEAR' }), [])

  const toggleCompareMode = useCallback(
    () => dispatch({ type: 'SET_MODE', compareMode: !compareMode }),
    [compareMode]
  )

  const openPanel = useCallback(
    () => dispatch({ type: 'SET_PANEL_OPEN', isPanelOpen: true }),
    []
  )

  const closePanel = useCallback(
    () => dispatch({ type: 'SET_PANEL_OPEN', isPanelOpen: false }),
    []
  )

  const value = useMemo<CompareSelectionContextValue>(
    () => ({
      compareMode,
      selectedIds,
      selectedListings,
      atLimit,
      isPanelOpen,
      toggleCompareMode,
      toggleListing,
      clearSelection,
      openPanel,
      closePanel,
    }),
    [
      compareMode,
      selectedIds,
      selectedListings,
      atLimit,
      isPanelOpen,
      toggleCompareMode,
      toggleListing,
      clearSelection,
      openPanel,
      closePanel,
    ]
  )

  return (
    <CompareSelectionContext.Provider value={value}>
      {children}
    </CompareSelectionContext.Provider>
  )
}
